import React, { ReactNode, Dispatch, useContext, useCallback } from "react";
import { StringObjectType } from "../StringObjectType";

interface WizardResultContextProviderProps<T extends StringObjectType<T>> {
  result: T;
  setResult: Dispatch<(input: T) => void>;
  children: ReactNode[] | ReactNode;
}

const WizardResultContext = React.createContext<unknown>({});
const WizardResultDispatchContext = React.createContext<
  Dispatch<(input: unknown) => undefined> | undefined
>(undefined);

/**
 * React hook to get the full result of the Wizard.
 * NOT RECOMMEND FOR USE DIRECTLY!
 * Recommended to use "useStepResult" hook when possible.
 *
 * @export
 * @template T
 * @returns {[
 *   T,
 *   Dispatch<(input: T) => void>
 * ]}
 */
export function useWizardResultContext<T extends StringObjectType<T>>(): [
  T,
  Dispatch<(input: T) => void>
] {
  const dispatchContext = useContext(WizardResultDispatchContext);
  if (dispatchContext === undefined) {
    throw new Error("Step dispatch context must be defined.");
  }

  return [
    useContext(WizardResultContext) as T,
    dispatchContext as Dispatch<(input: T) => void>,
  ];
}

/**
 * React hook to get the result of a step in the Wizard.
 *
 * @export
 * @template TResult
 * @template TStepId
 * @param {TStepId} stepId
 * @returns {[TResult[TStepId], (stepResult: TResult[TStepId]) => void]}
 */
export function useStepResult<
  TResult extends StringObjectType<TResult>,
  TStepId extends Extract<keyof TResult, string>
>(stepId: TStepId): [TResult[TStepId], (stepResult: TResult[TStepId]) => void] {
  const [result, updateResult] = useWizardResultContext<TResult>();

  const stepResult = result[stepId];

  const setStepResult = useCallback(
    (newStepResult: TResult[TStepId]) => {
      updateResult((totalResult) => {
        totalResult[stepId] = newStepResult;
      });
    },
    [stepId, updateResult]
  );

  return [stepResult, setStepResult];
}

/**
 * React component that wraps WizardResultContext.
 *
 * @template T
 * @param {WizardResultContextProviderProps<T>} {
 *   result,
 *   setResult,
 *   children,
 * }
 * @returns {JSX.Element}
 */
function WizardResultContextProvider<T extends StringObjectType<T>>({
  result,
  setResult,
  children,
}: WizardResultContextProviderProps<T>): JSX.Element {
  return (
    <WizardResultDispatchContext.Provider value={setResult}>
      <WizardResultContext.Provider value={result}>
        {children}
      </WizardResultContext.Provider>
    </WizardResultDispatchContext.Provider>
  );
}

export default WizardResultContextProvider;
